import { z } from 'zod';
import type { CombatReport, IntelReport, PlayerView, TurnMission } from '@war-game/engine';
import * as S from './schemas.js';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface Endpoint<
  Body extends z.ZodType = z.ZodType,
  Query extends z.ZodType = z.ZodType,
  Res = unknown,
> {
  method: HttpMethod;
  path: string;
  body?: Body;
  query?: Query;
  /** Schema da resposta (validado nos testes de integração). Ausente = tipo apenas. */
  response?: z.ZodType<Res>;
  auth: boolean;
  /** Marca o tipo da resposta para o cliente. */
  _res?: Res;
}

function ep<Res, Body extends z.ZodType = z.ZodNever, Query extends z.ZodType = z.ZodNever>(
  e: Omit<Endpoint<Body, Query, Res>, '_res'>,
): Endpoint<Body, Query, Res> {
  return e;
}

const Ok = z.object({ ok: z.literal(true) });
type Ok = z.infer<typeof Ok>;
type ActionOk = z.infer<typeof S.ActionResponse>;

export interface MatchDetail extends S.MatchSummary {
  config: S.CreateMatchBody;
  phase: 'PLANNING' | 'RESOLVING';
}

export interface ResearchInfo {
  completed: string[];
  active: Array<{ id: string; researchId: string; centerId: string; startsAt: number; endsAt: number }>;
  available: string[];
  era: number;
}

export interface DiplomacyInfo {
  relations: Array<{
    playerId: string;
    state: string;
    pending: { state: string; effectiveTurn: number } | null;
  }>;
  proposals: PlayerView['proposals'];
}

export interface AdminMatch {
  id: string;
  name: string;
  status: string;
  phase: string;
  currentTurn: number;
  nextTurnAt: string | null;
  players: number;
  paused: boolean;
  /** Governos da partida (substituir por bot). */
  roster: Array<{
    playerId: string;
    username: string;
    government: string;
    isBot: boolean;
    status: string;
  }>;
  /** Turnos com snapshot disponível (rollback, download e replay). */
  snapshots: number[];
}

/** Snapshot de turno (estado + ordens antes da virada), com o prazo usado na resolução, para replay. */
export interface TurnSnapshot {
  matchId: string;
  turn: number;
  /** Instante (ms) passado a `resolveTurn` como prazo; `null` em snapshots antigos. */
  deadline: number | null;
  state: unknown;
  createdAt: string;
}

export interface MultiAccountAlert {
  matchId: string;
  users: string[];
  signal: 'ip' | 'fingerprint';
  value: string;
  createdAt: string;
}

/** Contrato REST (prefixo /api/v1). */
/** Combates: um turno (padrão: o último) ou, com `all=1`, o histórico do jogador (mais recentes primeiro). */
export const CombatsQuery = z.object({
  turn: z.coerce.number().int().optional(),
  all: z.coerce.number().int().min(0).max(1).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

export const api = {
  // Auth e conta
  register: ep<S.AuthResponse, typeof S.RegisterBody>({
    method: 'POST',
    path: '/auth/register',
    body: S.RegisterBody,
    response: S.AuthResponse,
    auth: false,
  }),
  login: ep<S.AuthResponse, typeof S.LoginBody>({
    method: 'POST',
    path: '/auth/login',
    body: S.LoginBody,
    response: S.AuthResponse,
    auth: false,
  }),
  refresh: ep<S.AuthResponse>({
    method: 'POST',
    path: '/auth/refresh',
    response: S.AuthResponse,
    auth: false,
  }),
  logout: ep<Ok>({ method: 'POST', path: '/auth/logout', response: Ok, auth: false }),
  verifyPhone: ep<S.AuthResponse, typeof S.VerifyPhoneBody>({
    method: 'POST',
    path: '/auth/verify-phone',
    body: S.VerifyPhoneBody,
    response: S.AuthResponse,
    auth: true,
  }),
  resendCode: ep<Ok>({ method: 'POST', path: '/auth/resend-code', response: Ok, auth: true }),
  forgotPassword: ep<Ok, typeof S.ForgotPasswordBody>({
    method: 'POST',
    path: '/auth/forgot-password',
    body: S.ForgotPasswordBody,
    response: Ok,
    auth: false,
  }),
  resetPassword: ep<Ok, typeof S.ResetPasswordBody>({
    method: 'POST',
    path: '/auth/reset-password',
    body: S.ResetPasswordBody,
    response: Ok,
    auth: false,
  }),
  me: ep<S.User>({ method: 'GET', path: '/me', response: S.UserSchema, auth: true }),
  changePhone: ep<S.User, typeof S.PhoneBody>({
    method: 'PATCH',
    path: '/me/phone',
    body: S.PhoneBody,
    response: S.UserSchema,
    auth: true,
  }),
  setSmsAlerts: ep<S.User, typeof S.SmsAlertsBody>({
    method: 'PATCH',
    path: '/me/sms-alerts',
    body: S.SmsAlertsBody,
    response: S.UserSchema,
    auth: true,
  }),
  changePassword: ep<Ok, typeof S.ChangePasswordBody>({
    method: 'PATCH',
    path: '/me/password',
    body: S.ChangePasswordBody,
    response: Ok,
    auth: true,
  }),
  deleteMe: ep<Ok>({ method: 'DELETE', path: '/me', response: Ok, auth: true }),
  time: ep<z.infer<typeof S.TimeResponse>>({
    method: 'GET',
    path: '/time',
    response: S.TimeResponse,
    auth: false,
  }),

  // Partidas
  listMatches: ep<S.MatchSummary[]>({
    method: 'GET',
    path: '/matches',
    response: z.array(S.MatchSummarySchema),
    auth: true,
  }),
  createMatch: ep<S.MatchSummary, typeof S.CreateMatchBody>({
    method: 'POST',
    path: '/matches',
    body: S.CreateMatchBody,
    response: S.MatchSummarySchema,
    auth: true,
  }),
  getMatch: ep<MatchDetail>({ method: 'GET', path: '/matches/:id', auth: true }),
  joinMatch: ep<S.MatchSummary, typeof S.JoinMatchBody>({
    method: 'POST',
    path: '/matches/:id/join',
    body: S.JoinMatchBody,
    response: S.MatchSummarySchema,
    auth: true,
  }),
  startMatch: ep<S.MatchSummary, typeof S.StartMatchBody>({
    method: 'POST',
    path: '/matches/:id/start',
    body: S.StartMatchBody,
    response: S.MatchSummarySchema,
    auth: true,
  }),
  nextTurn: ep<Ok>({
    method: 'POST',
    path: '/matches/:id/next-turn',
    response: Ok,
    auth: true,
  }),
  matchByCode: ep<S.MatchSummary>({
    method: 'GET',
    path: '/matches/code/:code',
    response: S.MatchSummarySchema,
    auth: true,
  }),

  // Governo
  updateGovernment: ep<ActionOk, typeof S.GovernmentPatch>({
    method: 'PATCH',
    path: '/matches/:id/government',
    body: S.GovernmentPatch,
    response: S.ActionResponse,
    auth: true,
  }),
  renameTerritory: ep<ActionOk, typeof S.RenameTerritoryBody>({
    method: 'PATCH',
    path: '/matches/:id/territories/:tid/name',
    body: S.RenameTerritoryBody,
    response: S.ActionResponse,
    auth: true,
  }),

  // Estado
  view: ep<PlayerView>({ method: 'GET', path: '/matches/:id/view', auth: true }),
  territory: ep<PlayerView['territories'][string]>({
    method: 'GET',
    path: '/matches/:id/territories/:tid',
    auth: true,
  }),

  // Ordens e missões
  orders: ep<PlayerView['orders'][string]>({ method: 'GET', path: '/matches/:id/orders', auth: true }),
  putOrder: ep<ActionOk, typeof S.OrderSchema>({
    method: 'PUT',
    path: '/matches/:id/orders/:location',
    body: S.OrderSchema,
    response: S.ActionResponse,
    auth: true,
  }),
  deleteOrder: ep<ActionOk>({
    method: 'DELETE',
    path: '/matches/:id/orders/:location',
    response: S.ActionResponse,
    auth: true,
  }),
  createMission: ep<ActionOk, typeof S.MissionBody>({
    method: 'POST',
    path: '/matches/:id/missions',
    body: S.MissionBody,
    response: S.ActionResponse,
    auth: true,
  }),
  deleteMission: ep<ActionOk>({
    method: 'DELETE',
    path: '/matches/:id/missions/:mid',
    response: S.ActionResponse,
    auth: true,
  }),
  missions: ep<TurnMission[]>({ method: 'GET', path: '/matches/:id/missions', auth: true }),

  // Logística
  routes: ep<PlayerView['routes']>({ method: 'GET', path: '/matches/:id/routes', auth: true }),
  createRoute: ep<ActionOk, typeof S.RouteBody>({
    method: 'POST',
    path: '/matches/:id/routes',
    body: S.RouteBody,
    response: S.ActionResponse,
    auth: true,
  }),
  updateRoute: ep<ActionOk, typeof S.RoutePatch>({
    method: 'PATCH',
    path: '/matches/:id/routes/:rid',
    body: S.RoutePatch,
    response: S.ActionResponse,
    auth: true,
  }),
  deleteRoute: ep<ActionOk>({
    method: 'DELETE',
    path: '/matches/:id/routes/:rid',
    response: S.ActionResponse,
    auth: true,
  }),

  // Construção, produção, pesquisa
  construct: ep<ActionOk, typeof S.ConstructBody>({
    method: 'POST',
    path: '/matches/:id/territories/:tid/constructions',
    body: S.ConstructBody,
    response: S.ActionResponse,
    auth: true,
  }),
  cancelConstruction: ep<ActionOk>({
    method: 'DELETE',
    path: '/matches/:id/territories/:tid/constructions/:cid',
    response: S.ActionResponse,
    auth: true,
  }),
  produce: ep<ActionOk, typeof S.ProduceBody>({
    method: 'POST',
    path: '/matches/:id/production',
    body: S.ProduceBody,
    response: S.ActionResponse,
    auth: true,
  }),
  cancelProduction: ep<ActionOk>({
    method: 'DELETE',
    path: '/matches/:id/production/:pid',
    response: S.ActionResponse,
    auth: true,
  }),
  research: ep<ResearchInfo>({ method: 'GET', path: '/matches/:id/research', auth: true }),
  startResearch: ep<ActionOk, typeof S.ResearchBody>({
    method: 'POST',
    path: '/matches/:id/research',
    body: S.ResearchBody,
    response: S.ActionResponse,
    auth: true,
  }),

  // Inteligência
  intelMission: ep<ActionOk, typeof S.IntelMissionBody>({
    method: 'POST',
    path: '/matches/:id/intel/missions',
    body: S.IntelMissionBody,
    response: S.ActionResponse,
    auth: true,
  }),
  intelReports: ep<IntelReport[]>({ method: 'GET', path: '/matches/:id/intel/reports', auth: true }),

  // Diplomacia e comércio
  diplomacy: ep<DiplomacyInfo>({ method: 'GET', path: '/matches/:id/diplomacy', auth: true }),
  propose: ep<ActionOk, typeof S.ProposalBody>({
    method: 'POST',
    path: '/matches/:id/diplomacy/proposals',
    body: S.ProposalBody,
    response: S.ActionResponse,
    auth: true,
  }),
  declareWar: ep<ActionOk, typeof S.WarBody>({
    method: 'POST',
    path: '/matches/:id/diplomacy/war',
    body: S.WarBody,
    response: S.ActionResponse,
    auth: true,
  }),
  tradeOffers: ep<PlayerView['tradeOffers']>({
    method: 'GET',
    path: '/matches/:id/trade-offers',
    auth: true,
  }),
  createTradeOffer: ep<ActionOk, typeof S.TradeOfferBody>({
    method: 'POST',
    path: '/matches/:id/trade-offers',
    body: S.TradeOfferBody,
    response: S.ActionResponse,
    auth: true,
  }),

  // Ação genérica em tempo real (mercado, respostas, agentes, satélites...)
  action: ep<ActionOk, typeof S.RealtimeActionSchema>({
    method: 'POST',
    path: '/matches/:id/actions',
    body: S.RealtimeActionSchema,
    response: S.ActionResponse,
    auth: true,
  }),

  // Notícias, chat, placar, combates
  news: ep<Array<z.infer<typeof S.NewsItemSchema>>, z.ZodNever, typeof S.NewsQuery>({
    method: 'GET',
    path: '/matches/:id/news',
    query: S.NewsQuery,
    response: z.array(S.NewsItemSchema),
    auth: true,
  }),
  communique: ep<ActionOk, typeof S.CommuniqueBody>({
    method: 'POST',
    path: '/matches/:id/news/communiques',
    body: S.CommuniqueBody,
    response: S.ActionResponse,
    auth: true,
  }),
  messages: ep<S.ChatMessage[], z.ZodNever, z.ZodObject<{ channel: z.ZodOptional<z.ZodString> }>>({
    method: 'GET',
    path: '/matches/:id/messages',
    query: z.object({ channel: z.string().optional() }),
    response: z.array(S.ChatMessageSchema),
    auth: true,
  }),
  sendMessage: ep<S.ChatMessage, typeof S.MessageBody>({
    method: 'POST',
    path: '/matches/:id/messages',
    body: S.MessageBody,
    response: S.ChatMessageSchema,
    auth: true,
  }),
  favorites: ep<S.Favorites>({
    method: 'GET',
    path: '/matches/:id/favorites',
    response: S.FavoritesSchema,
    auth: true,
  }),
  addFavorite: ep<S.Favorites>({
    method: 'PUT',
    path: '/matches/:id/favorites/:tid',
    response: S.FavoritesSchema,
    auth: true,
  }),
  removeFavorite: ep<S.Favorites>({
    method: 'DELETE',
    path: '/matches/:id/favorites/:tid',
    response: S.FavoritesSchema,
    auth: true,
  }),
  scoreboard: ep<Array<z.infer<typeof S.ScoreEntrySchema>>>({
    method: 'GET',
    path: '/matches/:id/scoreboard',
    response: z.array(S.ScoreEntrySchema),
    auth: true,
  }),
  combats: ep<CombatReport[], z.ZodNever, typeof CombatsQuery>({
    method: 'GET',
    path: '/matches/:id/combats',
    query: CombatsQuery,
    auth: true,
  }),

  // Admin
  adminMatches: ep<AdminMatch[]>({ method: 'GET', path: '/admin/matches', auth: true }),
  adminResolve: ep<Ok>({ method: 'POST', path: '/admin/matches/:id/resolve', response: Ok, auth: true }),
  adminPause: ep<Ok>({ method: 'POST', path: '/admin/matches/:id/pause', response: Ok, auth: true }),
  adminResume: ep<Ok>({ method: 'POST', path: '/admin/matches/:id/resume', response: Ok, auth: true }),
  adminRollback: ep<Ok>({
    method: 'POST',
    path: '/admin/matches/:id/rollback/:turn',
    response: Ok,
    auth: true,
  }),
  adminDeleteMatch: ep<Ok>({ method: 'DELETE', path: '/admin/matches/:id', response: Ok, auth: true }),
  adminBot: ep<Ok>({ method: 'POST', path: '/admin/matches/:id/players/:pid/bot', response: Ok, auth: true }),
  adminSnapshot: ep<TurnSnapshot>({
    method: 'GET',
    path: '/admin/matches/:id/snapshots/:turn',
    auth: true,
  }),
  adminQueuesSession: ep<{ url: string }>({ method: 'POST', path: '/admin/queue-board/session', auth: true }),
  adminAlerts: ep<MultiAccountAlert[]>({ method: 'GET', path: '/admin/alerts', auth: true }),
  adminPutLayout: ep<S.TownLayout, typeof S.TownLayoutSchema>({
    method: 'PUT',
    path: '/admin/layouts/:tid',
    body: S.TownLayoutSchema,
    response: S.TownLayoutSchema,
    auth: true,
  }),
  adminDeleteLayout: ep<Ok>({ method: 'DELETE', path: '/admin/layouts/:tid', response: Ok, auth: true }),

  // Traçado das cidades (posição das instalações e estradas), editado no admin
  layouts: ep<S.TownLayouts>({
    method: 'GET',
    path: '/layouts',
    response: S.TownLayoutsSchema,
    auth: true,
  }),

  // Web Push
  pushKey: ep<{ publicKey: string | null }>({ method: 'GET', path: '/push/key', auth: true }),
  pushSubscribe: ep<
    Ok,
    z.ZodObject<{ endpoint: z.ZodString; keys: z.ZodObject<{ p256dh: z.ZodString; auth: z.ZodString }> }>
  >({
    method: 'POST',
    path: '/push/subscribe',
    body: z.object({ endpoint: z.string().url(), keys: z.object({ p256dh: z.string(), auth: z.string() }) }),
    response: Ok,
    auth: true,
  }),
} as const;

export type Api = typeof api;
export type EndpointName = keyof Api;
export type BodyOf<K extends EndpointName> =
  Api[K] extends Endpoint<infer B, z.ZodType, unknown>
    ? B extends z.ZodNever
      ? undefined
      : z.input<B>
    : never;
export type QueryOf<K extends EndpointName> =
  Api[K] extends Endpoint<z.ZodType, infer Q, unknown>
    ? Q extends z.ZodNever
      ? undefined
      : z.input<Q>
    : never;
export type ResponseOf<K extends EndpointName> =
  Api[K] extends Endpoint<z.ZodType, z.ZodType, infer R> ? R : never;

export const API_PREFIX = '/api/v1';

/** Monta o caminho substituindo `:param`. */
export function buildPath(path: string, params: Record<string, string | number> = {}): string {
  return path.replace(/:([a-zA-Z]+)/g, (_, key: string) => {
    const value = params[key];
    if (value === undefined) throw new Error(`Parâmetro ausente: ${key}`);
    return encodeURIComponent(String(value));
  });
}
